import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  where,
} from "firebase/firestore";
import { NAFED_COLLECTIONS } from "./nafedCollections";

export const WORK_STAGES = Object.freeze([
  { key: "SURVEY", label: "Site Survey", prerequisites: [] },
  { key: "SOIL_TEST", label: "Soil Investigation", prerequisites: [] },
  {
    key: "DESIGN",
    label: "Architectural & Structural Design",
    prerequisites: ["SURVEY", "SOIL_TEST"],
  },
  { key: "ESTIMATE", label: "Detailed Estimate & BOQ", prerequisites: ["DESIGN"] },
  { key: "DPR_CA", label: "Final DPR & Financial Appraisal", prerequisites: ["ESTIMATE"] },
]);

const INACTIVE_STATUSES = new Set([
  "DELETED",
  "REMOVED",
  "TRASHED",
  "ARCHIVED",
  "CANCELLED",
  "SOFT_DELETED",
  "PURGED",
]);

export const clean = (value = "") => String(value ?? "").trim();
export const upper = (value = "") => clean(value).toUpperCase();

export function isDeleted(row = {}) {
  return (
    row.isDeleted === true ||
    row.deleted === true ||
    row.isArchived === true ||
    row.archived === true ||
    Boolean(row.deletedAt || row.archivedAt || row.removedAt || row.purgedAt) ||
    [
      row.status,
      row.applicationStatus,
      row.recordStatus,
      row.allocationStatus,
    ].some((status) => INACTIVE_STATUSES.has(upper(status)))
  );
}

export function isNafedProject(row = {}) {
  return (
    !isDeleted(row) &&
    row.nafedProject === true &&
    upper(row.projectSource) === "NAFED" &&
    upper(row.applicationPortal) === "NAFED_PACS_APPLY"
  );
}

export function isMouExecuted(row = {}) {
  const values = [
    row.mouStatus,
    row.mouStage,
    row.mouExecutionStatus,
    row.workflowStage,
    row.currentStage,
    row.projectStage,
    row.status,
  ].map(upper);
  const signatures = row.mouSignatures || {};
  const pmcSigned = upper(signatures.pmc?.status) === "SIGNED";
  const pacsSigned = upper(signatures.pacs?.status) === "SIGNED";

  return (
    row.mouFullyExecuted === true ||
    row.twoPartyMouExecuted === true ||
    row.threePartyMouExecuted === true ||
    Boolean(
      row.finalExecutedMouUrl ||
        row.finalMouUrl ||
        row.finalSignedMouUrl ||
        row.mouFinalPdfUrl,
    ) ||
    (pmcSigned && pacsSigned) ||
    values.some((value) =>
      [
        "MOU_FULLY_EXECUTED",
        "FULLY_EXECUTED",
        "TWO_PARTY_MOU_FULLY_EXECUTED",
        "THREE_PARTY_MOU_FULLY_EXECUTED",
        "MOU_COMPLETED",
      ].includes(value),
    )
  );
}

export function getPmcUid(row = {}) {
  return clean(
    row.allocatedPmcUid ||
      row.assignedPmcUid ||
      row.pmcUid ||
      row.allocatedPmcId ||
      row.assignedPmcId ||
      row.pmcId,
  );
}

export const documentUrl = (record = {}) =>
  clean(
    record.url ||
      record.downloadURL ||
      record.downloadUrl ||
      record.fileUrl ||
      record.signedFileUrl,
  );

/**
 * Reads every historical Assurance Letter shape but returns one canonical
 * record. This keeps PACS, PMC and Registrar screens compatible during rollout.
 */
export function getNafedAssuranceLetter(row = {}) {
  const candidates = [
    row.pmcVerificationDocuments?.NAFED_ASSURANCE_LETTER,
    row.pmcTechnicalDocuments?.NAFED_ASSURANCE_LETTER,
    row.nafedAssuranceLetter,
    row.hiringAssuranceLetter,
    row.assuranceLetter,
  ];
  const record = candidates.find((item) => documentUrl(item)) || {};
  const url =
    documentUrl(record) ||
    clean(
      row.nafedAssuranceLetterUrl ||
        row.hiringAssuranceLetterUrl ||
        row.assuranceLetterUrl,
    );
  if (!url) return null;
  return {
    ...record,
    type: "NAFED_ASSURANCE_LETTER",
    code: record.code || "NAL",
    title: record.title || "NAFED Assurance Letter",
    url,
  };
}

const timestampValue = (value) => {
  if (!value) return 0;
  if (typeof value?.toMillis === "function") return value.toMillis();
  if (Number.isFinite(Number(value?.seconds))) return Number(value.seconds) * 1000;
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
};

const activeTask = (item = {}) => !isDeleted(item);

export function stageState(tasks = [], stageKey) {
  const task = tasks
    .filter((item) => upper(item.type) === upper(stageKey) && activeTask(item))
    .sort(
      (a, b) =>
        timestampValue(b.updatedAt || b.createdAt) -
        timestampValue(a.updatedAt || a.createdAt),
    )[0];
  return task
    ? upper(task.pmcStatus || task.adminStatus || task.status)
    : "NOT_ALLOCATED";
}

export function canAllocateStage(tasks = [], stageKey) {
  const normalizedKey = upper(stageKey);
  const stage = WORK_STAGES.find((item) => item.key === normalizedKey);
  if (!stage) return false;
  if (
    tasks.some(
      (item) => upper(item.type) === normalizedKey && activeTask(item),
    )
  ) {
    return false;
  }
  return stage.prerequisites.every(
    (requiredStage) => stageState(tasks, requiredStage) === "APPROVED",
  );
}

export const nextAvailableStage = (tasks = []) =>
  WORK_STAGES.find((stage) => canAllocateStage(tasks, stage.key)) || null;

export const availableStages = (tasks = []) =>
  WORK_STAGES.filter((stage) => canAllocateStage(tasks, stage.key));

export function dedupeStageTasks(tasks = []) {
  const byStage = new Map();
  const duplicates = [];
  [...tasks]
    .filter(activeTask)
    .sort(
      (a, b) =>
        timestampValue(b.updatedAt || b.createdAt) -
        timestampValue(a.updatedAt || a.createdAt),
    )
    .forEach((item) => {
      const key = upper(item.type);
      if (!key) return;
      if (!byStage.has(key)) byStage.set(key, item);
      else duplicates.push(item);
    });
  return { tasks: [...byStage.values()], duplicates };
}

export function mapsUrl(row = {}) {
  if (clean(row.googleMapsUrl)) return clean(row.googleMapsUrl);
  const latitude = row.latitude ?? row.committeeEvidence?.latitude;
  const longitude = row.longitude ?? row.committeeEvidence?.longitude;
  if (
    latitude !== undefined &&
    latitude !== null &&
    longitude !== undefined &&
    longitude !== null
  ) {
    return `https://www.google.com/maps?q=${encodeURIComponent(
      `${latitude},${longitude}`,
    )}`;
  }
  const address = [
    row.village || row.locationVillage,
    row.taluka || row.block,
    row.district || row.districtName,
    row.state || row.stateName,
  ]
    .filter(Boolean)
    .join(", ");
  return address
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`
    : "";
}

/**
 * Creates or updates the technical projection only after the current two-party
 * MOU (or a compatible historical three-party MOU) is fully executed.
 * sourceProjects must already be scoped to the logged-in PMC.
 */
export async function syncEligibleNafedProjects(
  db,
  { sourceProjects = [], pmcUid = "" } = {},
) {
  if (!Array.isArray(sourceProjects)) {
    throw new Error("sourceProjects must be an array.");
  }
  const safePmcUid = clean(pmcUid);
  const synced = [];

  for (const source of sourceProjects) {
    if (
      !source?.id ||
      !isNafedProject(source) ||
      !isMouExecuted(source) ||
      !getPmcUid(source) ||
      (safePmcUid && getPmcUid(source) !== safePmcUid)
    ) {
      continue;
    }

    const assurance = getNafedAssuranceLetter(source);
    const payload = {
      sourceProjectId: source.id,
      nafedProject: true,
      projectSource: "NAFED",
      applicationPortal: "NAFED_PACS_APPLY",
      title:
        source.projectName ||
        source.title ||
        source.societyName ||
        source.pacsName ||
        "NAFED PACS Project",
      pacsUid:
        source.pacsUid ||
        source.pacsUserUid ||
        source.pacsUserId ||
        source.pacsId ||
        source.applicantUid ||
        source.submittedByUid ||
        source.createdByUid ||
        source.profileId ||
        "",
      pacsEmail: source.pacsEmail || source.applicantEmail || source.email || "",
      pacsName:
        source.pacsName || source.societyName || source.organizationName || "",
      applicationId: source.applicationId || source.projectId || source.id,
      capacityMt: Number(
        source.capacityMt ||
          source.capacityMT ||
          source.capacity ||
          source.godownCapacity ||
          0,
      ),
      state: source.state || source.stateName || "",
      district: source.district || source.districtName || "",
      taluka: source.taluka || source.block || "",
      locationVillage: source.village || source.locationVillage || "",
      address: source.address || source.fullAddress || "",
      googleMapsUrl: mapsUrl(source),
      allocatedPmcUid: getPmcUid(source),
      allocatedPmcName:
        source.allocatedPmcName || source.assignedPmcName || source.pmcName || "",
      mouStatus: "FULLY_EXECUTED",
      finalExecutedMouUrl:
        source.finalExecutedMouUrl ||
        source.finalMouUrl ||
        source.finalSignedMouUrl ||
        source.mouFinalPdfUrl ||
        source.executedMouUrl ||
        "",
      nafedAssuranceStatus: source.nafedAssuranceStatus || "PENDING",
      ...(assurance
        ? {
            nafedAssuranceLetter: assurance,
            nafedAssuranceLetterUrl: assurance.url,
          }
        : {}),
      status: "MOU_COMPLETED",
      syncStatus: "ACTIVE",
      updatedAt: serverTimestamp(),
    };

    await setDoc(
      doc(db, NAFED_COLLECTIONS.workflowProjects, source.id),
      payload,
      { merge: true },
    );
    synced.push({ id: source.id, ...payload });
  }
  return synced;
}

export async function loadProjectTasks(db, projectId) {
  const safeProjectId = clean(projectId);
  if (!safeProjectId) return [];
  const snap = await getDocs(
    query(
      collection(db, NAFED_COLLECTIONS.workflowTasks),
      where("projectId", "==", safeProjectId),
    ),
  );
  return snap.docs.map((item) => ({ id: item.id, ...item.data() }));
}

export async function queueWorkflowMail(
  db,
  { to, subject, html, event, projectId },
) {
  const recipients = (Array.isArray(to) ? to : [to])
    .map(clean)
    .filter(Boolean);
  if (!recipients.length) return null;
  const ref = doc(collection(db, NAFED_COLLECTIONS.mailQueue));
  await setDoc(ref, {
    to: recipients,
    message: { subject: clean(subject), html: String(html || "") },
    metadata: { event: clean(event), projectId: clean(projectId) },
    deliveryStatus: "QUEUED",
    createdAt: serverTimestamp(),
  });
  return ref.id;
}

export async function getProfile(db, uid) {
  const safeUid = clean(uid);
  if (!safeUid) return null;
  const snap = await getDoc(doc(db, NAFED_COLLECTIONS.users, safeUid));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}
